import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS, STATUS_COLORS } from "../../lib/api";
import { DashboardLayout, StatCard } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Euro, TrendingUp, ShoppingBag, Users, Bike, Store, Percent, Tag, Download, BadgeCheck, Briefcase, Target, Sparkles, CalendarDays, Map as MapIcon, Wallet, Rocket, Crown, Award, Bot, Eye, Bug, ScanLine, Satellite } from "lucide-react";import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

function EconomicsPanel() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/ceo/economics?period=${period}`).then(({ data }) => setData(data)).catch(() => setData(null)); }, [period]);
  const eur = (v) => "€" + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const growth = (c, p) => { if (!p) return null; return ((c - p) / p) * 100; };
  const cur = data?.current, prev = data?.previous;
  const PERIODS = [["today", "Oggi"], ["week", "Settimana"], ["month", "Mese"], ["year", "Anno"]];
  const STREAMS = [["marketplace_commission", "Marketplace Commission"], ["delivery", "Delivery Revenue"], ["fuma_plus", "Fuma+ Revenue"], ["business_subscription", "Business Subscription Revenue"], ["advertising", "Advertising Revenue"], ["bookings", "Booking Revenue"], ["service_fee", "Service fee (Other)"]];
  const Grow = ({ c, p }) => { const g = growth(c, p); if (g === null) return null; const up = g >= 0; return <span className={`text-xs font-semibold ${up ? "text-[#00E676]" : "text-red-400"}`}>{up ? "▲" : "▼"} {Math.abs(g).toFixed(0)}%</span>; };
  return (
    <div className="space-y-5" data-testid="economics-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-xl flex items-center gap-2"><Euro className="h-5 w-5 text-[#00E676]" /> Dashboard Economica</h3>
          <p className="text-sm text-zinc-500">Quanto valore del territorio passa attraverso Fuma e quanto ne monetizza.</p>
        </div>
        <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`econ-period-${v}`} onClick={() => setPeriod(v)} className={`px-3 py-1.5 rounded-full text-sm ${period === v ? "bg-[#00E676] text-black font-semibold" : "text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>
      {!data ? <div className="text-zinc-500 py-10 text-center">Caricamento…</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5" data-testid="econ-gmv">
              <div className="text-xs uppercase tracking-widest text-zinc-500">GMV (volume totale)</div>
              <div className="text-3xl font-black mt-1">{eur(cur.gmv)}</div>
              <div className="mt-1"><Grow c={cur.gmv} p={prev.gmv} /> <span className="text-xs text-zinc-500">vs periodo prec.</span></div>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-[#00E676]/15 to-transparent border border-[#00E676]/30 p-5" data-testid="econ-revenue">
              <div className="text-xs uppercase tracking-widest text-[#00E676]">Revenue Fuma</div>
              <div className="text-3xl font-black mt-1">{eur(cur.revenue)}</div>
              <div className="mt-1"><Grow c={cur.revenue} p={prev.revenue} /> <span className="text-xs text-zinc-500">vs periodo prec.</span></div>
            </div>
            <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5" data-testid="econ-takerate">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Take Rate</div>
              <div className="text-3xl font-black mt-1">{Number(cur.take_rate || 0).toFixed(1)}%</div>
              <div className="mt-1 text-xs text-zinc-500">{cur.orders} ordini · {cur.bookings} prenotazioni</div>
            </div>
          </div>
          <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5">
            <div className="text-sm font-semibold mb-3">Revenue Stack — fonti di ricavo</div>
            <div className="space-y-2">
              {STREAMS.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between text-sm" data-testid={`econ-stream-${k}`}>
                  <span className="text-zinc-400">{l}</span>
                  <span className="font-semibold">{eur(cur.streams?.[k])}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-4">Fuma+ e Business Subscription sono ricavi ricorrenti (MRR). Advertising si popola con le campagne locali (in arrivo).</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5" data-testid="econ-aov">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Valore medio ordine (AOV)</div>
              <div className="text-2xl font-black mt-1">{eur(cur.avg_order_value)}</div>
              <div className="mt-1"><Grow c={cur.avg_order_value} p={prev.avg_order_value} /> <span className="text-xs text-zinc-500">vs prec.</span></div>
            </div>
            {[["free", "Clienti Free"], ["plus", "Clienti Fuma+"]].map(([k, l]) => (
              <div key={k} className={`rounded-2xl border p-5 ${k === "plus" ? "bg-gradient-to-br from-[#FFD700]/12 to-transparent border-[#FFD700]/30" : "bg-[#1C1C1F] border-white/10"}`} data-testid={`econ-seg-${k}`}>
                <div className={`text-xs uppercase tracking-widest ${k === "plus" ? "text-[#FFD700]" : "text-zinc-500"}`}>{l}</div>
                <div className="text-2xl font-black mt-1">{eur(data.segments?.[k]?.gmv)}</div>
                <div className="text-xs text-zinc-500 mt-1">{data.segments?.[k]?.customers || 0} clienti · {data.segments?.[k]?.orders || 0} ordini · AOV {eur(data.segments?.[k]?.avg_spend)}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5" data-testid="econ-top-businesses">
              <div className="text-sm font-semibold mb-3">Top attività per GMV</div>
              <div className="space-y-2">
                {(data.top_businesses || []).slice(0, 8).map((bz, i) => (
                  <div key={bz.business_id} className="flex items-center justify-between text-sm" data-testid={`econ-topbiz-${i}`}>
                    <span className="text-zinc-400 truncate">{i + 1}. {bz.name || "—"} <span className="text-zinc-600">· {bz.orders} ord. · TR {Number(bz.take_rate || 0).toFixed(0)}%</span></span>
                    <span className="font-semibold whitespace-nowrap">{eur(bz.gmv)}</span>
                  </div>
                ))}
                {(!data.top_businesses || data.top_businesses.length === 0) && <div className="text-zinc-600 text-sm">Nessun dato nel periodo.</div>}
              </div>
            </div>
            <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-5" data-testid="econ-top-customers">
              <div className="text-sm font-semibold mb-3">Top clienti per GMV</div>
              <div className="space-y-2">
                {(data.top_customers || []).slice(0, 8).map((c, i) => (
                  <div key={c.customer_id} className="flex items-center justify-between text-sm" data-testid={`econ-topcust-${i}`}>
                    <span className="text-zinc-400 truncate">{i + 1}. {c.name || "—"} <span className="text-zinc-600">· {c.orders} ord.</span></span>
                    <span className="font-semibold whitespace-nowrap">{eur(c.gmv)}</span>
                  </div>
                ))}
                {(!data.top_customers || data.top_customers.length === 0) && <div className="text-zinc-600 text-sm">Nessun dato nel periodo.</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

import BusinessVerification from "../../components/BusinessVerification";
import ProVerification from "../../components/ProVerification";
import RidersLiveMap from "../../components/RidersLiveMap";
import CeoMissions from "../../components/CeoMissions";
import CeoAssistant from "../../components/CeoAssistant";
import CeoEvents from "../../components/CeoEvents";
import TerritoryMap from "../../components/TerritoryMap";
import Payouts from "../../components/Payouts";
import RoadmapDashboard from "../../components/RoadmapDashboard";
import ExperiencesCeoPanel from "../../components/ceo/ExperiencesCeoPanel";
import ServicesModerationPanel from "../../components/services/ServicesModerationPanel";
import JobsCeoPanel from "../../components/JobsCeoPanel";
import DealsCeoPanel from "../../components/ceo/DealsCeoPanel";
import BusinessSubsCeoPanel from "../../components/ceo/BusinessSubsCeoPanel";
import AdvertisingCeoPanel from "../../components/ceo/AdvertisingCeoPanel";
import GrowthCeoPanel from "../../components/ceo/GrowthCeoPanel";
import GrowthOpportunitiesPanel from "../../components/ceo/GrowthOpportunitiesPanel";
import GameAnalyticsPanel from "../../components/ceo/GameAnalyticsPanel";
import SponsoredMissionsPanel from "../../components/ceo/SponsoredMissionsPanel";
import RewardsCeoPanel from "../../components/ceo/RewardsCeoPanel";
import OpsCatalogPanel from "../../components/ceo/OpsCatalogPanel";
import PassportsCeoPanel from "../../components/ceo/PassportsCeoPanel";
import GeoAuditPanel from "../../components/ceo/GeoAuditPanel";
import MapAdminPanel from "../../components/ceo/MapAdminPanel";
import AiOperatorPanel from "../../components/ceo/AiOperatorPanel";
import UxIssuesPanel from "../../components/ceo/UxIssuesPanel";
import PreviewClientePanel from "../../components/ceo/PreviewClientePanel";
import UxAuditPanel from "../../components/ceo/UxAuditPanel";
import RadarPanel from "../../components/ceo/RadarPanel";
import { toast } from "sonner";

const NAV = [
  { key: "analytics", label: "Analisi", path: "/ceo", icon: TrendingUp },
  { key: "radar", label: "Radar Attività", path: "/ceo/radar", icon: Satellite },
  { key: "preview", label: "Preview Cliente", path: "/ceo/preview", icon: Eye },
  { key: "uxissues", label: "UX Issues", path: "/ceo/uxissues", icon: Bug },
  { key: "uxaudit", label: "Auto-Audit UX", path: "/ceo/uxaudit", icon: ScanLine },
  { key: "economia", label: "Economia", path: "/ceo/economia", icon: Euro },
  { key: "deals", label: "Fuma+ Deals", path: "/ceo/deals", icon: Crown },
  { key: "b2b", label: "Business B2B", path: "/ceo/b2b", icon: Store },
  { key: "advertising", label: "Advertising", path: "/ceo/advertising", icon: Rocket },
  { key: "growth", label: "Growth Engine", path: "/ceo/growth", icon: TrendingUp },
  { key: "opportunities", label: "Growth Score", path: "/ceo/opportunities", icon: TrendingUp },
  { key: "game", label: "Rider Run", path: "/ceo/game", icon: TrendingUp },
  { key: "rewards", label: "Rewards & Livelli", path: "/ceo/rewards", icon: Award },
  { key: "passports", label: "Passaporti Fuma", path: "/ceo/passports", icon: Award },
  { key: "roadmap", label: "Roadmap Go Live", path: "/ceo/roadmap", icon: Rocket },
  { key: "lavoro", label: "Lavoro", path: "/ceo/lavoro", icon: Briefcase },
  { key: "experiences", label: "Esperienze AI", path: "/ceo/experiences", icon: Sparkles },
  { key: "servizi", label: "Servizi Esperienze", path: "/ceo/servizi", icon: Sparkles },
  { key: "opscatalog", label: "Menu Attività", path: "/ceo/opscatalog", icon: Store },
  { key: "geoaudit", label: "Verifica Posizioni", path: "/ceo/geoaudit", icon: Store },
  { key: "mapadmin", label: "Gestisci Mappa", path: "/ceo/mapadmin", icon: MapIcon },
  { key: "map", label: "Mappa live", path: "/ceo/map", icon: Bike },
  { key: "verification", label: "Verifica attività", path: "/ceo/verification", icon: BadgeCheck },
  { key: "professionals", label: "Professionisti", path: "/ceo/professionals", icon: Briefcase },
  { key: "users", label: "Utenti", path: "/ceo/users", icon: Users },
  { key: "coupons", label: "Coupon", path: "/ceo/coupons", icon: Tag },
  { key: "commissions", label: "Commissioni", path: "/ceo/commissions", icon: Percent },
  { key: "liquidazioni", label: "Liquidazioni", path: "/ceo/liquidazioni", icon: Wallet },
  { key: "missions", label: "Missioni", path: "/ceo/missions", icon: Target },
  { key: "events", label: "Eventi", path: "/ceo/events", icon: CalendarDays },
  { key: "livemap", label: "Mappa viva", path: "/ceo/livemap", icon: MapIcon },
  { key: "assistant", label: "Assistente IA", path: "/ceo/assistant", icon: Sparkles },
  { key: "aiop", label: "Assistente Operativo", path: "/ceo/aiop", icon: Bot },
];

const COLORS = ["#00E676", "#3B82F6", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#EC4899", "#10B981"];

function Analytics() {
  const [a, setA] = useState(null);
  const load = () => api.get("/ceo/analytics").then(({ data }) => setA(data));
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const exportCsv = () => {
    const rows = [["Giorno","Ordini","Fatturato"], ...a.daily.map((d) => [d.day, d.orders, d.revenue])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a"); link.href = url; link.download = "fuma_report.csv"; link.click();
    toast.success("Report esportato");
  };
  if (!a) return null;
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-zinc-400"><span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#00E676]" /></span> Dati live · aggiornamento ogni 8s</div>
        <Button data-testid="export-data" onClick={exportCsv} variant="secondary"><Download className="h-4 w-4 mr-1" /> Esporta dati</Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="ceo-revenue" label="Fatturato totale" value={`€${a.revenue}`} accent icon={Euro} />
        <StatCard testid="ceo-revenue-today" label="Fatturato oggi" value={`€${a.revenue_today ?? 0}`} accent icon={Euro} />
        <StatCard testid="ceo-commissions" label="Commissioni (12%)" value={`€${a.commissions}`} icon={Percent} />
        <StatCard testid="ceo-avg-ticket" label="Ticket medio" value={`€${a.avg_ticket}`} icon={TrendingUp} />
        <StatCard testid="ceo-orders" label="Ordini totali" value={a.orders_total} icon={ShoppingBag} />
        <StatCard testid="ceo-orders-today" label="Ordini oggi" value={a.orders_today} icon={ShoppingBag} />
        <StatCard testid="ceo-orders-live" label="Ordini live" value={a.orders_live ?? 0} accent icon={ShoppingBag} />
        <StatCard testid="ceo-avg-late" label="Consegne in ritardo" value={a.deliveries_late} icon={Bike} accent={a.deliveries_late > 0} />
        <StatCard testid="ceo-businesses-online" label="Attività online" value={`${a.businesses_online ?? a.businesses}/${a.businesses}`} icon={Store} accent />
        <StatCard testid="ceo-riders-online" label="Rider online" value={a.riders_online ?? a.riders_active} accent icon={Bike} />
        <StatCard testid="ceo-customers" label="Clienti registrati" value={a.customers} icon={Users} />
        <StatCard testid="ceo-delivered" label="Consegne completate" value={a.deliveries_completed} icon={Bike} />
      </div>

      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#00E676] animate-pulse" /> Ordini live</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 text-left border-b border-white/10"><th className="p-2">Ordine</th><th className="p-2">Attività</th><th className="p-2">Cliente</th><th className="p-2">Totale</th><th className="p-2">Stato</th><th className="p-2">Orario</th></tr></thead>
            <tbody>
              {(a.recent_orders || []).map((o) => (
                <tr key={o.id} data-testid={`ceo-live-order-${o.id}`} className="border-b border-white/5">
                  <td className="p-2">#{o.id.slice(-5)}</td><td className="p-2">{o.business_name}</td><td className="p-2">{o.customer_name}</td>
                  <td className="p-2 text-[#00E676]">€{o.total.toFixed(2)}</td>
                  <td className="p-2"><Badge className={`border ${STATUS_COLORS[o.status] || ""}`}>{STATUS_LABELS[o.status] || o.status}</Badge></td>
                  <td className="p-2 text-zinc-500">{new Date(o.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Fatturato ultimi 7 giorni</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={a.daily}><XAxis dataKey="day" stroke="#71717a" fontSize={12} /><YAxis stroke="#71717a" fontSize={12} /><Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} /><Line type="monotone" dataKey="revenue" stroke="#00E676" strokeWidth={2} dot={{ fill: "#00E676" }} /></LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Ordini per giorno</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={a.daily}><XAxis dataKey="day" stroke="#71717a" fontSize={12} /><YAxis stroke="#71717a" fontSize={12} /><Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} /><Bar dataKey="orders" fill="#3B82F6" radius={[4,4,0,0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Zone più attive</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart><Pie data={a.top_zones} dataKey="orders" nameKey="zone" cx="50%" cy="50%" outerRadius={80} label={(e) => e.zone}>{a.top_zones.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip contentStyle={{ background: "#1C1C1F", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} /></PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
          <h3 className="font-semibold mb-4">Top attività & rider</h3>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Attività con più ordini</div>
          {a.top_businesses.map((b, i) => <div key={i} className="flex justify-between text-sm py-1"><span>{b.name}</span><span className="text-[#00E676]">{b.orders}</span></div>)}
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 mt-4">Rider con più consegne</div>
          {a.top_riders.map((r, i) => <div key={i} className="flex justify-between text-sm py-1"><span>{r.name}</span><span className="text-[#00E676]">{r.deliveries}</span></div>)}
        </div>
      </div>
    </div>
  );
}

function UsersMgmt() {
  const [users, setUsers] = useState([]);
  const [role, setRole] = useState("");
  const load = async () => { const { data } = await api.get("/users", { params: role ? { role } : {} }); setUsers(data); };
  useEffect(() => { load(); }, [role]);
  const suspend = async (id, val) => { await api.put(`/admin/suspend/${id}`, { suspended: val }); load(); toast.success(val ? "Sospeso" : "Riattivato"); };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["", "customer", "business", "rider"].map((r) => <Button key={r} size="sm" variant={role === r ? "default" : "secondary"} data-testid={`filter-role-${r || "all"}`} onClick={() => setRole(r)} className={role === r ? "bg-[#00E676] text-black" : ""}>{r === "" ? "Tutti" : r}</Button>)}
      </div>
      <div className="overflow-x-auto bg-[#1C1C1F] border border-white/10 rounded-lg">
        <table className="w-full text-sm"><thead><tr className="border-b border-white/10 text-zinc-500 text-left"><th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Ruolo</th><th className="p-3">Stato</th><th className="p-3">Azioni</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5">
              <td className="p-3">{u.name}</td><td className="p-3">{u.email}</td><td className="p-3">{u.role}</td>
              <td className="p-3">{u.suspended ? <span className="text-red-400">sospeso</span> : <span className="text-[#00E676]">attivo</span>}</td>
              <td className="p-3">{["ceo","admin"].includes(u.role) ? "—" : <Button size="sm" variant={u.suspended ? "secondary" : "destructive"} data-testid={`toggle-suspend-${u.id}`} onClick={() => suspend(u.id, !u.suspended)}>{u.suspended ? "Riattiva" : "Sospendi"}</Button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function CouponsMgmt() {
  const [coupons, setCoupons] = useState([]);
  const [form, setForm] = useState({ code: "", discount_percent: "", discount_amount: "", expires_at: "" });
  const [editId, setEditId] = useState(null);
  const load = async () => { const { data } = await api.get("/coupons"); setCoupons(data); };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ code: "", discount_percent: "", discount_amount: "", expires_at: "" }); setEditId(null); };
  const startEdit = (c) => { setEditId(c.id); setForm({ code: c.code, discount_percent: c.discount_percent || "", discount_amount: c.discount_amount || "", expires_at: c.expires_at ? String(c.expires_at).slice(0, 10) : "" }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const add = async () => { if (form.discount_percent && form.discount_amount) { toast.error("Imposta lo sconto in % OPPURE in €, non entrambi"); return; } if (!form.code.trim()) { toast.error("Inserisci un codice"); return; } const payload = { code: form.code, discount_percent: parseFloat(form.discount_percent) || 0, discount_amount: parseFloat(form.discount_amount) || 0, expires_at: form.expires_at || null }; try { if (editId) { await api.patch(`/coupons/${editId}`, payload); toast.success("Coupon aggiornato"); } else { await api.post("/coupons", { ...payload, active: true }); toast.success("Coupon creato"); } resetForm(); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = async (c) => { try { await api.patch(`/coupons/${c.id}`, { active: !c.active }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (c) => { if (!window.confirm(`Eliminare il coupon ${c.code}?`)) return; try { await api.delete(`/coupons/${c.id}`); load(); toast.success("Coupon eliminato"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const expired = (c) => c.expires_at && new Date(c.expires_at) < new Date();
  return (
    <div>
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-5 mb-6 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
        <div><label className="text-sm">Codice</label><Input data-testid="ceo-coupon-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><label className="text-sm">Sconto %</label><Input data-testid="ceo-coupon-percent" type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><label className="text-sm">Sconto €</label><Input data-testid="ceo-coupon-amount" type="number" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
        <div><label className="text-sm">Scadenza</label><Input data-testid="ceo-coupon-expiry" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="bg-[#0A0A0C] border-white/10 mt-1 text-zinc-400" /></div>
        <div className="flex gap-2">
          <Button data-testid="ceo-create-coupon" onClick={add} className="bg-[#00E676] text-black rounded-md flex-1">{editId ? "Salva modifica" : "Crea coupon"}</Button>
          {editId && <Button data-testid="ceo-coupon-cancel" variant="outline" onClick={resetForm} className="rounded-md">Annulla</Button>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">{coupons.map((c) => (
        <div key={c.id} data-testid={`coupon-${c.id}`} className={`bg-[#1C1C1F] border rounded-lg p-4 ${c.active && !expired(c) ? "border-dashed border-[#00E676]/40" : "border-white/10 opacity-70"}`}>
          <div className="flex items-center justify-between">
            <div className="font-display font-bold text-lg text-[#00E676]">{c.code}</div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${expired(c) ? "text-amber-400 border-amber-400/40" : c.active ? "text-[#00E676] border-[#00E676]/40" : "text-zinc-500 border-white/15"}`}>{expired(c) ? "Scaduto" : c.active ? "Attivo" : "Disattivato"}</span>
          </div>
          <div className="text-sm text-zinc-400">{c.discount_percent ? `${c.discount_percent}%` : `€${c.discount_amount}`}{c.expires_at ? ` · fino ${String(c.expires_at).slice(0, 10)}` : ""}</div>
          <div className="flex gap-2 mt-3">
            <Button data-testid={`coupon-edit-${c.id}`} size="sm" variant="outline" onClick={() => startEdit(c)} className="text-xs min-h-[44px] flex-1">Modifica</Button>
            <Button data-testid={`coupon-toggle-${c.id}`} size="sm" variant="outline" onClick={() => toggle(c)} className="text-xs min-h-[44px] flex-1">{c.active ? "Disattiva" : "Attiva"}</Button>
            <Button data-testid={`coupon-delete-${c.id}`} size="sm" variant="destructive" onClick={() => del(c)} className="text-xs min-h-[44px] flex-1">Elimina</Button>
          </div>
        </div>
      ))}</div>
    </div>
  );
}

function Commissions() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/ceo/delivery-pricing").then(({ data }) => setP(data)); }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/ceo/delivery-pricing", {
        base_fee: parseFloat(p.base_fee), included_km: parseFloat(p.included_km),
        per_km: parseFloat(p.per_km), max_distance_km: parseFloat(p.max_distance_km), surcharge: parseFloat(p.surcharge),
      });
      setP(data); toast.success("Tariffe aggiornate");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Field = ({ k, label, unit }) => (
    <div>
      <label className="text-sm">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <Input data-testid={`pricing-${k}`} type="number" step="0.1" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} className="bg-[#0A0A0C] border-white/10" />
        <span className="text-zinc-500 text-sm w-14">{unit}</span>
      </div>
    </div>
  );
  if (!p) return null;
  return (
    <div className="space-y-6">
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold mb-2">Modello economico</h3>
        <div className="text-sm text-zinc-400 space-y-1"><div>Commissione piattaforma: <span className="text-[#00E676] font-semibold">12%</span> sugli ordini</div><div>Costo servizio: <span className="text-white">3%</span> sul subtotale</div></div>
      </div>
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6">
        <h3 className="font-semibold mb-1">Tariffe di consegna — calcolo automatico per distanza</h3>
        <p className="text-sm text-zinc-400 mb-4">La consegna viene calcolata sul percorso reale attività → cliente. Nessuna tariffa fissa.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field k="base_fee" label="Costo base" unit="€" />
          <Field k="included_km" label="Km inclusi" unit="km" />
          <Field k="per_km" label="Costo km extra" unit="€/km" />
          <Field k="max_distance_km" label="Distanza max (zona servita)" unit="km" />
          <Field k="surcharge" label="Supplemento" unit="€" />
        </div>
        <Button data-testid="save-pricing" onClick={save} className="mt-4 bg-[#00E676] text-black rounded-md">Salva tariffe</Button>
      </div>
    </div>
  );
}

export default function CeoDashboard() {
  return (
    <DashboardLayout title="Dashboard CEO / Admin" nav={NAV}>
      <Routes>
        <Route index element={<Analytics />} />
        <Route path="economia" element={<EconomicsPanel />} />
        <Route path="deals" element={<DealsCeoPanel />} />
        <Route path="b2b" element={<BusinessSubsCeoPanel />} />
        <Route path="advertising" element={<AdvertisingCeoPanel />} />
        <Route path="growth" element={<GrowthCeoPanel />} />
        <Route path="opportunities" element={<GrowthOpportunitiesPanel />} />
        <Route path="game" element={<div className="space-y-10"><GameAnalyticsPanel /><SponsoredMissionsPanel /></div>} />
        <Route path="rewards" element={<RewardsCeoPanel />} />
        <Route path="passports" element={<PassportsCeoPanel />} />
        <Route path="roadmap" element={<RoadmapDashboard />} />
        <Route path="lavoro" element={<JobsCeoPanel />} />
        <Route path="experiences" element={<ExperiencesCeoPanel />} />
        <Route path="servizi" element={<ServicesModerationPanel />} />
        <Route path="opscatalog" element={<OpsCatalogPanel />} />
        <Route path="geoaudit" element={<GeoAuditPanel />} />
        <Route path="mapadmin" element={<MapAdminPanel />} />
        <Route path="map" element={<div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-6"><h3 className="font-semibold mb-4 flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-[#00E676] animate-pulse" /> Mappa live rider</h3><RidersLiveMap /></div>} />
        <Route path="verification" element={<BusinessVerification />} />
        <Route path="professionals" element={<ProVerification />} />
        <Route path="users" element={<UsersMgmt />} />
        <Route path="coupons" element={<CouponsMgmt />} />
        <Route path="commissions" element={<Commissions />} />
        <Route path="liquidazioni" element={<Payouts />} />
        <Route path="missions" element={<CeoMissions />} />
        <Route path="events" element={<CeoEvents />} />
        <Route path="livemap" element={<div className="space-y-3"><h3 className="font-semibold flex items-center gap-2"><MapIcon className="h-5 w-5 text-[#00E676]" /> Mappa viva del territorio</h3><TerritoryMap height={560} /></div>} />
        <Route path="assistant" element={<CeoAssistant />} />
        <Route path="aiop" element={<AiOperatorPanel />} />
        <Route path="preview" element={<PreviewClientePanel />} />
        <Route path="uxissues" element={<UxIssuesPanel />} />
        <Route path="uxaudit" element={<UxAuditPanel />} />
        <Route path="radar" element={<RadarPanel />} />
      </Routes>
    </DashboardLayout>
  );
}
